import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DataStore } from '../../@services/data/data-store.service';

/**
 * Bandeau « Hors ligne, données du 28/10 à 08:12 » (NA-05) : l'écran montre
 * les dernières données gardées au lieu de la panne, et dit de quand elles
 * datent. Il rappelle que rien ne s'enregistre sans réseau : les écritures ne
 * sont pas mises en attente. Invisible tant que le réseau répond.
 *
 * Usage, en haut de l'écran : `<vw-offline-banner (retry)="load()"></vw-offline-banner>`
 *
 * Un écran qui n'a pas de données gardées (Willy) l'allume lui-même avec
 * `[active]` et ses propres textes : même bandeau ambre partout où le réseau
 * manque, au lieu d'un bandeau noir qui ressemblait à un message passager.
 * `<vw-offline-banner [active]="isOffline" titleKey="…" messageKey="…" (retry)="…" />`
 *
 * Le titre et la phrase reçoivent `{{date}}` et `{{time}}` des données gardées ;
 * `messageParams` les complète ou les remplace, par exemple l'heure des
 * données que l'écran affiche vraiment (« Hors ligne, données de 07:40 ») :
 * `<vw-offline-banner titleKey="components.state.offline.title-time" [messageParams]="{ time: '07:40' }" />`
 */
@Component({
  selector: 'vw-offline-banner',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './offline-banner.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class OfflineBannerComponent {
  private readonly data = inject(DataStore);
  private readonly translate = inject(TranslateService);

  @Output() retry = new EventEmitter<void>();
  /** Allumé par l'écran lui-même ; sans valeur, le bandeau suit les données gardées (`offlineSince`). */
  @Input() active: boolean | null = null;
  /** Titre (clé i18n) ; par défaut « Hors ligne, données du … ». */
  @Input() titleKey = 'components.state.offline.title';
  /** Phrase (clé i18n) ; par défaut « Rien ne s'enregistre sans réseau. ». */
  @Input() messageKey = 'components.state.offline.message';
  /** Paramètres du titre et de la phrase, par-dessus `{{date}}` et `{{time}}` des données gardées. */
  @Input() messageParams: Record<string, string | number> | null = null;

  readonly since = this.data.offlineSince;

  get visible(): boolean {
    return this.active ?? !!this.since();
  }

  /** Ce que reçoivent le titre et la phrase : le moment des données, puis les paramètres de l'écran. */
  get params(): Record<string, string | number> {
    return { ...this.when, ...this.messageParams };
  }

  /** Jour et heure des données, au format de la langue de l'app. */
  get when(): { date: string; time: string } {
    const at = new Date(this.since() ?? 0);
    const lang = this.translate.getCurrentLang() || 'fr';
    return {
      date: at.toLocaleDateString(lang, { day: '2-digit', month: '2-digit' }),
      time: at.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
    };
  }
}
